import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  InfoIcon,
  GraduationCapIcon,
  LandmarkIcon,
  ShieldCheckIcon,
  CalendarCheckIcon,
  StarIcon,
  CheckCircle2Icon,
  MailIcon,
  HandshakeIcon,
  ClipboardCheckIcon,
  HelpCircleIcon,
  ChevronDownIcon,
} from 'lucide-react'
import { Link } from 'react-router-dom'
import { Container } from '../../components/ui/Container'
import { Button } from '../../components/ui/Button'
import { SubpageHero } from '../../components/sections/subpages/SubpageHero'
import { PatternDecor } from '../../components/ui/PatternDecor'
import { FaqAccordion } from '../../components/sections/faq/FaqAccordion'
const overviewCards = [
  {
    title: "What's included:",
    items: [
      'Monthly expert sessions (live + Q&A)',
      'Parent-only discussions (moderated, safe space)',
      'Practical guides & resources',
      'Access to recorded content',
      'Priority support & updates',
    ],
  },
  {
    title: "Who it's for:",
    items: [
      'Parents of enrolled children',
      'Parents planning to enroll children',
      'Muslim families living in non-Muslim countries',
    ],
  },
  {
    title: 'How it works:',
    items: [
      'Join and get immediate access to the resource library',
      'Attend monthly sessions and ask questions',
      'Use ready-to-apply tools for school and daily situations',
    ],
  },
]
const includedBlocks = [
  {
    icon: InfoIcon,
    title: 'Legal & Rights Support',
    body: 'Legal guidance and rights awareness to help parents understand options and respond calmly.',
    items: [
      'Rights & next-step guidance (what to do first)',
      'Message templates for schools and institutions',
      'Meeting scripts and calm escalation pathways',
      'Checklists for documentation and follow-up',
    ],
  },
  {
    icon: GraduationCapIcon,
    title: 'Access to Experts (rotational)',
    body: 'Parents pay for access — not for hours.',
    items: [
      'Child psychologist (monthly slot)',
      'Education advisor',
      'Integration & rights specialist',
      'Experienced parent mentors',
    ],
  },
  {
    icon: LandmarkIcon,
    title: 'School & Institution Toolkit',
    body: 'A complete set of ready-to-use materials that helps you handle school situations faster, with less stress and more confidence.',
    items: [
      'Step-by-step response pathways for common situations',
      'Pre-written messages for calm communication',
      'Decision checklists: when to escalate — and when not to',
      'Documentation guide: what to save and how to follow up',
    ],
  },
  {
    icon: ShieldCheckIcon,
    title: 'Safe Parent Community (moderated)',
    body: 'Not a random chat. A structured, strongly moderated community with cases and solutions.',
    items: [],
  },
  {
    icon: CalendarCheckIcon,
    title: 'Monthly Parent Sessions',
    body: '',
    items: [
      'Identity & confidence',
      'Peer pressure',
      'Digital safety',
      'Family communication',
    ],
  },
  {
    icon: StarIcon,
    title: 'Membership Benefits',
    body: '',
    items: [
      'Priority access to kids programs',
      '−20% on Adult Programs',
      'Early access to scholarships',
      'Invitations to closed events',
    ],
  },
]
// "Choose the level of support" pricing section is temporarily hidden. Set to true to restore it.
const SHOW_PRICING_SECTION = false

export function Membership() {
  return (
    <>
      <SubpageHero
        eyebrow="Parent Protection & Support Membership"
        title="Support system — not a course"
        paragraphs={[
          'Parent Membership provides guidance, legal awareness, and community-based support — helping families navigate education, housing, and institutional challenges with confidence.',
          "Because when your child faces a problem, you shouldn't face it alone.",
        ]}
        primaryCta={{
          label: 'Join Membership',
          to: '/en/waitlist',
        }}
        secondaryCta={{
          label: "See What's Included",
          to: '#whats-included',
        }}
        image="https://cdn.magicpatterns.com/uploads/oUxdhXVW1hzbvktagMKhFk/Membership.jpg"
        imageAlt="Muslim parents in a moderated group discussion with laptops"
        compact
        masked
      />

      {/* What Membership gives you */}
      <section
        id="whats-included"
        className="relative py-16 md:py-24 overflow-hidden isolate scroll-mt-24 bg-sage-50/60"
      >
        <PatternDecor side="right" y="top" size="lg" />
        <Container>
          <h2 className="font-display text-3xl md:text-5xl text-ink-900 leading-tight">
            What Membership gives you
          </h2>
          <p className="mt-5 text-ink-700 leading-relaxed">
            Membership helps parents understand the challenges their children
            face, respond calmly and confidently, protect family values, and
            avoid isolation and confusion.
          </p>

          <div className="mt-10 grid md:grid-cols-3 gap-5">
            {overviewCards.map((card, i) => (
              <motion.div
                key={card.title}
                initial={{
                  opacity: 0,
                  y: 16,
                }}
                whileInView={{
                  opacity: 1,
                  y: 0,
                }}
                viewport={{
                  once: true,
                  margin: '-60px',
                }}
                transition={{
                  duration: 0.5,
                  delay: i * 0.06,
                }}
                className={`rounded-2xl border border-ink-900/5 p-6 shadow-soft ${['bg-sage-100', 'bg-[#F7EDE9]', 'bg-sand-100'][i]}`}
              >
                <h3 className="font-display text-lg text-ink-900">
                  {card.title}
                </h3>
                <ul className="mt-5 space-y-3">
                  {card.items.map((item) => (
                    <li
                      key={item}
                      className="flex items-start gap-2.5 text-ink-700 text-sm"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-ink-700 mt-2 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>

          <div className="mt-8 rounded-2xl bg-sage-50 border border-sage-200 px-6 py-4 text-center">
            <p className="text-ink-500 italic">
              This is a support system — not parenting advice from theory.
            </p>
          </div>
        </Container>
      </section>

      {/* What you're really paying for */}
      <section className="py-16 md:py-24 bg-white/60">
        <Container>
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-6">
              <h2 className="font-display text-3xl md:text-5xl text-ink-900 leading-tight">
                What you're really paying for
              </h2>
              <p className="mt-5 text-ink-700 leading-relaxed">
                Parents don't pay for content. Parents pay for peace of mind,
                protection, and ready-to-use solutions.
              </p>
            </div>
            <div className="lg:col-span-6">
              <ul className="space-y-4">
                {[
                  'Legal guidance & rights awareness (non-emergency)',
                  'Expert on-call support (rotational access)',
                  'School & institution toolkit (templates + checklists)',
                  'Strongly moderated parent community',
                ].map((item) => (
                  <li
                    key={item}
                    className="flex items-start gap-3 text-ink-700"
                  >
                    <CheckCircle2Icon
                      size={18}
                      className="text-forest-700 mt-1 flex-shrink-0"
                      strokeWidth={1.8}
                    />
                    <span className="font-medium">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </Container>
      </section>

      {/* What's included */}
      <section className="py-16 md:py-24 bg-[#F7EDE9]/60">
        <Container>
          <h2 className="font-display text-3xl md:text-5xl text-ink-900 leading-tight">
            What's included
          </h2>
          <p className="mt-5 text-ink-700 leading-relaxed">
            Six building blocks designed to help you act calmly and confidently.
          </p>

          <div className="mt-10 grid md:grid-cols-3 gap-5">
            {includedBlocks.map((block, i) => (
              <motion.div
                key={block.title}
                initial={{
                  opacity: 0,
                  y: 16,
                }}
                whileInView={{
                  opacity: 1,
                  y: 0,
                }}
                viewport={{
                  once: true,
                  margin: '-60px',
                }}
                transition={{
                  duration: 0.5,
                  delay: i * 0.05,
                }}
                className={`rounded-2xl border border-ink-900/5 p-6 shadow-soft ${['bg-sage-100', 'bg-[#F7EDE9]', 'bg-sand-100', 'bg-[#EDF2F7]', 'bg-sage-100', 'bg-[#F7EDE9]'][i]}`}
              >
                <span className="w-11 h-11 inline-flex items-center justify-center rounded-xl bg-white border border-forest-700/10 text-forest-700">
                  <block.icon size={18} strokeWidth={1.6} />
                </span>
                <h3 className="mt-5 font-display text-lg text-ink-900">
                  {block.title}
                </h3>
                {block.body && (
                  <p className="mt-3 text-ink-700 text-sm leading-relaxed">
                    {block.body}
                  </p>
                )}
                {block.items.length > 0 && (
                  <ul className="mt-4 space-y-2.5">
                    {block.items.map((item) => (
                      <li
                        key={item}
                        className="flex items-start gap-2.5 text-ink-700 text-sm"
                      >
                        <CheckCircle2Icon
                          size={14}
                          className="text-forest-700 mt-0.5 flex-shrink-0"
                          strokeWidth={1.8}
                        />
                        {item}
                      </li>
                    ))}
                  </ul>
                )}
              </motion.div>
            ))}
          </div>
        </Container>
      </section>

      {/* Legal guidance */}
      <section className="py-16 md:py-24 bg-white/60">
        <Container>
          <h2 className="font-display text-3xl md:text-5xl text-ink-900 leading-tight">
            Legal guidance that helps you respond calmly
          </h2>
          <p className="mt-5 text-ink-700 leading-relaxed">
            Get clear next steps and rights awareness for common school and
            institution situations. This is non-emergency guidance — not legal
            representation.
          </p>

          <div className="mt-10 grid md:grid-cols-2 gap-5">
            <div className="rounded-2xl bg-white border border-sage-200 p-6 shadow-soft">
              <h3 className="font-display text-lg text-ink-900">
                How it works
              </h3>
              <ul className="mt-5 space-y-3">
                {[
                  'Submit your question (chat / written form)',
                  'Receive guidance + practical next steps',
                  'Use templates to communicate calmly and effectively',
                ].map((item) => (
                  <li
                    key={item}
                    className="flex items-start gap-2.5 text-ink-700 text-sm"
                  >
                    <CheckCircle2Icon
                      size={14}
                      className="text-forest-700 mt-0.5 flex-shrink-0"
                      strokeWidth={1.8}
                    />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-2xl bg-white border border-sage-200 p-6 shadow-soft">
              <h3 className="font-display text-lg text-ink-900">
                Most common situations
              </h3>
              <ul className="mt-5 space-y-3">
                {[
                  'Teacher misunderstandings and classroom incidents',
                  'School policies affecting religious practice (dress, fasting, prayer)',
                  'Bullying, exclusion, and discriminatory treatment',
                  'Parent–school communication breakdowns and complaint steps',
                ].map((item) => (
                  <li
                    key={item}
                    className="flex items-start gap-2.5 text-ink-700 text-sm"
                  >
                    <CheckCircle2Icon
                      size={14}
                      className="text-forest-700 mt-0.5 flex-shrink-0"
                      strokeWidth={1.8}
                    />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="mt-8 rounded-2xl bg-sage-100 border border-sage-200 px-6 py-4 text-center">
            <p className="text-ink-500 italic leading-relaxed">
              Important: this is not full legal defense, not court support, and
              not an emergency response — it is guidance and next steps.
            </p>
          </div>
        </Container>
      </section>

      {/* Inside the Toolkit */}
      <section className="relative py-16 md:py-24 overflow-hidden isolate bg-sand-50/60">
        <PatternDecor side="left" y="middle" size="md" />
        <Container>
          <h2 className="font-display text-3xl md:text-5xl text-ink-900 leading-tight">
            Inside the Toolkit
          </h2>
          <p className="mt-5 text-ink-700 leading-relaxed">
            Practical tools that reduce stress and save time.
          </p>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              {
                icon: MailIcon,
                title: 'School email templates',
                body: 'ready-to-send, respectful, effective',
              },
              {
                icon: HandshakeIcon,
                title: 'Meeting scripts',
                body: 'how to speak with teachers and administrators',
              },
              {
                icon: ClipboardCheckIcon,
                title: 'Complaint checklist',
                body: 'how to file a complaint correctly and calmly',
              },
              {
                icon: HelpCircleIcon,
                title: '"What to do if…" playbooks',
                body: 'exclusion, misunderstanding, peer pressure',
              },
            ].map((tool, i) => (
              <motion.div
                key={tool.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.5, delay: i * 0.06 }}
                className={`rounded-2xl border border-ink-900/5 p-6 shadow-soft ${['bg-sage-100', 'bg-[#F7EDE9]', 'bg-sand-100', 'bg-[#EDF2F7]'][i]}`}
              >
                <span className="w-11 h-11 inline-flex items-center justify-center rounded-xl bg-white border border-forest-700/10 text-forest-700">
                  <tool.icon size={18} strokeWidth={1.6} />
                </span>
                <h3 className="mt-5 font-display text-lg text-ink-900">
                  {tool.title}
                </h3>
                <p className="mt-2 text-ink-700 text-sm leading-relaxed">
                  {tool.body}
                </p>
              </motion.div>
            ))}
          </div>
        </Container>
      </section>

      {/* Community Housing Support */}
      <section className="py-16 md:py-24 bg-white/60">
        <Container>
          <h2 className="font-display text-3xl md:text-5xl text-ink-900 leading-tight">
            Community Housing Support
          </h2>
          <p className="mt-5 text-ink-700 leading-relaxed">
            Finding suitable housing is one of the most challenging issues faced
            by Muslim families living in non-Muslim countries.
          </p>

          <div className="mt-10 grid md:grid-cols-2 gap-8">
            <div>
              <p className="text-ink-700 leading-relaxed">
                As part of Parent Membership, we facilitate a community-based
                housing support space — a member-only environment where families
                can support each other during relocation and housing searches.
              </p>
              <p className="mt-4 text-ink-700 leading-relaxed">
                This initiative is based on peer support and shared
                responsibility, not commercial brokerage.
              </p>
            </div>
            <div>
              <h3 className="font-display text-lg text-ink-900">
                What members can do
              </h3>
              <ul className="mt-4 space-y-3">
                {[
                  'Share trusted rental opportunities',
                  'Post housing requests within the community',
                  'Exchange guidance and experience',
                  'Reduce uncertainty and isolation during relocation',
                ].map((item) => (
                  <li
                    key={item}
                    className="flex items-start gap-2.5 text-ink-700 text-sm"
                  >
                    <CheckCircle2Icon
                      size={14}
                      className="text-forest-700 mt-0.5 flex-shrink-0"
                      strokeWidth={1.8}
                    />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="mt-10 grid md:grid-cols-2 gap-8">
            <div>
              <h3 className="font-display text-lg text-ink-900">
                How it works
              </h3>
              <p className="mt-3 text-ink-700 leading-relaxed">
                Only for verified members • No commissions • No intermediary
                role • No guarantees
              </p>
            </div>
            <div>
              <h3 className="font-display text-lg text-ink-900">
                Why families value this
              </h3>
              <p className="mt-3 text-ink-700 leading-relaxed">
                It's not about listings — it's about access to a trusted, closed
                environment: people who have already rented, shared experience,
                recommendations, and safety.
              </p>
            </div>
          </div>

          <div className="mt-10 rounded-2xl bg-sage-100 border border-sage-200 px-6 py-4 text-center">
            <p className="text-ink-500 italic leading-relaxed">
              The organization does not act as an agent, broker, or guarantor.
              The platform facilitates peer-to-peer information sharing only.
            </p>
          </div>
        </Container>
      </section>

      {/* Choose the level of support — temporarily hidden, see SHOW_PRICING_SECTION */}
      {SHOW_PRICING_SECTION && (
      <section
        id="pricing"
        className="relative py-16 md:py-24 overflow-hidden isolate scroll-mt-24 bg-[#EDF2F7]/60"
      >
        <PatternDecor side="right" y="bottom" size="lg" />
        <Container>
          <h2 className="font-display text-3xl md:text-5xl text-ink-900 leading-tight">
            Choose the level of support
          </h2>
          <p className="mt-5 text-ink-700 leading-relaxed">
            Start with Basic or choose Protection for legal guidance and expert
            access. Annual plan offers best value for year-round support.
          </p>

          <div className="mt-10 grid md:grid-cols-3 gap-5">
            {[
              {
                badge: 'BASIC',
                badgeColor: 'bg-sage-100 text-forest-700',
                name: 'Basic Membership',
                price: '€29',
                period: '/ month',
                items: [
                  { text: 'Monthly parent sessions', prefix: '✓' },
                  { text: 'School & institution toolkit access', prefix: '✓' },
                  { text: 'Moderated parent community', prefix: '✓' },
                  { text: 'Member benefits & updates', prefix: '✓' },
                ],
                cta: 'Join Basic',
                highlight: false,
              },
              {
                badge: 'RECOMMENDED',
                badgeColor: 'bg-forest-700 text-ivory',
                name: 'Protection Membership',
                price: '€39',
                period: '/ month',
                items: [
                  { text: 'Everything in Basic', prefix: '✓' },
                  { text: 'Legal & rights guidance', prefix: '+' },
                  { text: 'Rotational access to experts', prefix: '+' },
                  { text: 'School & institution toolkit', prefix: '+' },
                ],
                cta: 'Join Protection',
                highlight: true,
              },
              {
                badge: 'BEST VALUE',
                badgeColor: 'bg-sand-400 text-ink-900',
                name: 'Protection (Annual)',
                price: '€390',
                period: '/ year',
                items: [
                  { text: 'Full membership for 12 months', prefix: '✓' },
                  { text: 'One payment, year-round support', prefix: '✓' },
                  { text: 'Priority updates & new releases', prefix: '✓' },
                  { text: 'Best value — 2 months free', prefix: '✓' },
                ],
                cta: 'Join Annual',
                highlight: false,
              },
            ].map((plan) => (
              <motion.div
                key={plan.name}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.5 }}
                className={`flex flex-col h-full rounded-2xl border p-7 shadow-soft ${
                  plan.highlight
                    ? 'border-forest-700 ring-1 ring-forest-700/20 bg-white'
                    : 'border-sage-200 bg-white'
                }`}
              >
                <span
                  className={`inline-block text-[10px] font-semibold uppercase tracking-[0.14em] px-3 py-1 rounded-full ${plan.badgeColor}`}
                >
                  {plan.badge}
                </span>
                <h3 className="mt-4 font-display text-lg text-ink-900">
                  {plan.name}
                </h3>
                <div className="mt-3 flex items-baseline gap-1">
                  <span className="font-display text-4xl text-ink-900">
                    {plan.price}
                  </span>
                  <span className="text-sm text-ink-500">{plan.period}</span>
                </div>
                <ul className="mt-6 space-y-3">
                  {plan.items.map((item) => (
                    <li
                      key={item.text}
                      className="flex items-start gap-2.5 text-ink-700 text-sm"
                    >
                      {item.prefix === '+' ? (
                        <span className="text-forest-700 font-bold text-base leading-none mt-0.5">
                          +
                        </span>
                      ) : (
                        <CheckCircle2Icon
                          size={14}
                          className="text-forest-700 mt-0.5 flex-shrink-0"
                          strokeWidth={1.8}
                        />
                      )}
                      {item.text}
                    </li>
                  ))}
                </ul>
                <Link to="/waitlist" className="mt-auto pt-6 inline-block">
                  <Button
                    variant={plan.highlight ? 'primary' : 'secondary'}
                    size="sm"
                  >
                    {plan.cta}
                  </Button>
                </Link>
              </motion.div>
            ))}
          </div>
        </Container>
      </section>
      )}

      {/* FAQ */}
      <section className="py-16 md:py-24 bg-white/60">
        <Container>
          <h2 className="font-display text-3xl md:text-5xl text-ink-900 leading-tight">
            Frequently asked questions
          </h2>
          <p className="mt-5 text-ink-700 leading-relaxed">
            Quick answers about membership, support, and how legal guidance
            works.
          </p>
          <FaqAccordion />
        </Container>
      </section>

      {/* For donors */}
      <section className="py-16 md:py-24 bg-sage-50/60">
        <Container>
          <h3 className="font-display text-lg text-ink-900">
            For donors and institutional partners
          </h3>
          <p className="mt-4 text-ink-700 leading-relaxed">
            Parent membership strengthens family resilience by providing access
            to guidance, rights awareness, and expert support, complementing
            child-focused educational programs.
          </p>
        </Container>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-24 bg-white/60">
        <Container>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8">
            <div className="max-w-xl">
              <h2 className="font-display text-3xl md:text-5xl text-ink-900 leading-tight">
                Join the Parent Membership
              </h2>
              <p className="mt-4 text-ink-700">
                Get guidance, practical tools, and a supportive community — and
                know you're not alone when something happens.
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <Link to="/en/waitlist">
                <Button variant="primary">Join Membership</Button>
              </Link>
              <Link to="/en/contact">
                <Button variant="secondary">Contact us</Button>
              </Link>
            </div>
          </div>
        </Container>
      </section>
    </>
  )
}
const faqItems = [
  {
    q: 'Is this legal representation?',
    a: 'No. This is non-emergency guidance and rights awareness — practical next steps, not court representation.',
  },
  {
    q: 'What topics can I ask about?',
    a: 'School misunderstandings, religious accommodations (dress, food, holidays), bullying or exclusion, and communication with institutions.',
  },
  {
    q: 'How do I submit a question?',
    a: 'Submit inside the member area (chat or written form). We respond with clear next steps and, when relevant, templates you can use.',
  },
  {
    q: 'Who provides expert support?',
    a: 'Expert access is rotational and may include a child psychologist, education advisor, integration/rights specialist, and experienced parent mentors.',
  },
  {
    q: 'Can I cancel my membership?',
    a: 'Yes. You can cancel anytime — membership is designed to support you for as long as you need it.',
  },
  {
    q: 'Does membership include benefits?',
    a: 'Yes. Members receive priority updates and selected benefits, including discounts on some programs.',
  },
]
function FaqAccordion() {
  const [openIndex, setOpenIndex] = useState<number | null>(0)
  return (
    <div className="mt-10 space-y-3">
      {faqItems.map((item, i) => {
        const isOpen = openIndex === i
        return (
          <div
            key={item.q}
            className={`rounded-2xl border transition-colors ${isOpen ? 'bg-white border-sage-200 shadow-soft' : 'bg-sage-100 border-transparent'}`}
          >
            <button
              type="button"
              onClick={() => setOpenIndex(isOpen ? null : i)}
              className="w-full flex items-center gap-4 px-6 py-5 text-left"
            >
              <span
                className={`w-9 h-9 rounded-full inline-flex items-center justify-center flex-shrink-0 transition-colors ${isOpen ? 'bg-sage-200 text-forest-700' : 'bg-sage-200/60 text-ink-500'}`}
              >
                <ChevronDownIcon
                  size={16}
                  strokeWidth={2}
                  className={`transition-transform ${isOpen ? 'rotate-180' : ''}`}
                />
              </span>
              <span className="font-display text-base text-ink-900">
                {item.q}
              </span>
            </button>
            <AnimatePresence>
              {isOpen && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
                  className="overflow-hidden"
                >
                  <div className="px-6 pb-5 pl-[4.25rem]">
                    <p className="text-ink-700 leading-relaxed">{item.a}</p>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )
      })}
    </div>
  )
}
